import type { MarketplaceListing } from "@/types/content";

/**
 * The public Devory Marketplace catalogue. Every entry below is grounded in
 * real repository evidence — no invented version numbers, publishers,
 * download counts, ratings or compatibility claims. Where the underlying
 * inventory is real but not yet individually browsable (most of it), the
 * listing describes the real aggregate state honestly rather than
 * fabricating individual products. See docs/CONTENT_GUIDE.md for how to
 * update this file and record the evidence source.
 *
 * This is a publicly browsable catalogue, not a completed commercial
 * Marketplace — browsing is public; installation, publishing and
 * commercial transactions remain limited to qualified or future workflows.
 */
export const marketplaceListings: MarketplaceListing[] = [
  {
    id: "uuid-generator",
    name: "UUID Generator",
    category: "component",
    description:
      "Devory's canonical Runtime-qualified component: the first to pass real, compiled, subprocess-execution qualification, and the package format's own canary.",
    publisher: "Devory",
    firstParty: true,
    compatibility: "Devory Runtime",
    status: "pilot",
    availability: "catalogue-preview",
    installable: false,
    qualification: "Runtime-qualified",
    detailPath: "/qualification",
    keycap: "qualify",
  },
  {
    id: "foundation-catalogue",
    name: "Foundation Component Catalogue",
    category: "component",
    description:
      "438 specified component identities across 22 technical categories, including application foundations, authentication, authorisation, API, data, AI and security. Seven have audited, Runtime-qualified implementations today; the rest remain specified, not yet built.",
    publisher: "Devory",
    firstParty: true,
    compatibility: "Devory Runtime",
    status: "in-development",
    availability: "catalogue-preview",
    installable: false,
    qualification: "7 of 438 Runtime-qualified",
    detailPath: "/components",
    keycap: "compose",
  },
  {
    id: "authentication-foundations",
    name: "Authentication Foundations",
    category: "collection",
    description:
      "The real, named collection grouping Devory's authentication-category components: sign-in, tokens, sessions, credential verification. Most member components remain specified rather than implemented.",
    publisher: "Devory",
    firstParty: true,
    compatibility: "Devory Runtime",
    status: "planned",
    availability: "planned",
    installable: false,
    detailPath: "/components",
    keycap: "identity",
  },
  {
    id: "listing-publication-workflow",
    name: "Marketplace Listing and Publication Workflow",
    category: "workflow",
    description:
      "The real, gateway-wired path from publisher identity through package upload, validation and publication to a browsable Marketplace listing. Free and internal-only listings can complete this path today; paid acquisition cannot yet.",
    publisher: "Devory",
    firstParty: true,
    compatibility: "Devory Cloud",
    status: "in-development",
    availability: "in-qualification",
    installable: false,
    detailPath: "/cloud",
    keycap: "publish",
  },
  {
    id: "aui-protocol-profile",
    name: "Devory Autonomous User Interface Protocol Profile",
    category: "agents-and-integrations",
    description:
      "The capability discovery, resource-scope and operation protocol foundation external AI agents use to request scoped, confirmed and audited operations on a user's behalf. No externally reachable network transport exists yet.",
    publisher: "Devory",
    firstParty: true,
    compatibility: "Devory Autonomous User Interface",
    status: "in-development",
    availability: "in-active-development",
    installable: false,
    detailPath: "/agent-user-interface",
    keycap: "ai",
  },
  {
    id: "qualification-profile",
    name: "Component Qualification Profile",
    category: "qualification-profile",
    description:
      "The real qualification pipeline that validates a component package through compiled fixtures and genuine subprocess execution across four languages (Rust, TypeScript, Python and Go) before it can be trusted.",
    publisher: "Devory",
    firstParty: true,
    compatibility: "Devory Runtime",
    status: "pilot",
    availability: "not-yet-installable",
    installable: false,
    detailPath: "/qualification",
    keycap: "qualify",
  },
];
